import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'
import { useRatingNudgeStore } from '@/store/ratingNudge'
import { fetchPlayed, rateGame } from '@/api/wishlist'
import { StarRating } from '@/components/ui/StarRating'
import { Icon } from '@/components/ui/Icon'
import type { PlayedEntryWithGame } from '@/types'

/** Epoch ms of a played entry's finish time; null/unparseable counts as 0. */
function finishedMs(entry: PlayedEntryWithGame): number {
  const t = entry.finishedAt ? Date.parse(entry.finishedAt) : NaN
  return Number.isNaN(t) ? 0 : t
}

export function RatingNudgeBanner() {
  const { user } = useAuthStore()
  const { activeGroupId } = useUIStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hiddenForever, dismissedThrough, suppressedGames, dismiss, hideForever } =
    useRatingNudgeStore()

  const { data: entries = [] } = useQuery({
    queryKey: ['played', activeGroupId],
    queryFn: () => fetchPlayed(activeGroupId!),
    enabled: !!activeGroupId,
  })

  const rateMutation = useMutation({
    mutationFn: ({ gameId, rating }: { gameId: string; rating: number }) =>
      rateGame(activeGroupId!, gameId, rating),
    onMutate: async ({ gameId, rating }) => {
      await queryClient.cancelQueries({ queryKey: ['played', activeGroupId] })
      const previous = queryClient.getQueryData<PlayedEntryWithGame[]>(['played', activeGroupId])
      queryClient.setQueryData<PlayedEntryWithGame[]>(['played', activeGroupId], (old = []) =>
        old.map((entry) => {
          if (entry.gameId !== gameId) return entry
          const ratings = { ...entry.ratings }
          if (rating === 0) delete ratings[user!.steamId]
          else ratings[user!.steamId] = rating
          return { ...entry, ratings }
        }),
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['played', activeGroupId], context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['played', activeGroupId] }),
  })

  // Most-recently-finished unrated-by-me games, newest first. Games the user
  // explicitly un-rated are suppressed so removing a rating doesn't re-nag.
  const unrated = useMemo(() => {
    const myId = user?.steamId
    if (!myId) return []
    const suppressed = new Set(activeGroupId ? suppressedGames[activeGroupId] ?? [] : [])
    return entries
      .filter((e) => !e.ratings[myId] && !suppressed.has(e.gameId))
      .sort((a, b) => finishedMs(b) - finishedMs(a))
  }, [entries, user?.steamId, activeGroupId, suppressedGames])

  const newestMs = unrated.length > 0 ? finishedMs(unrated[0]) : 0
  const watermark = activeGroupId ? dismissedThrough[activeGroupId] ?? -1 : -1
  const show = !hiddenForever && !!activeGroupId && unrated.length > 0 && newestMs > watermark

  if (!show) return null

  const current = unrated[0]

  return (
    <div className="cg-rating-banner">
      <span className="cg-rating-banner-icon" aria-hidden="true">
        <Icon name="star" size={16} />
      </span>
      <div className="cg-rating-banner-text">
        <span className="cg-rating-banner-title">
          You finished <strong>{current.game.title}</strong> — rate it?
        </span>
        <span className="cg-rating-banner-sub">
          Ratings make your group&apos;s recommendations more accurate.
        </span>
      </div>
      <div className="cg-rating-banner-stars">
        <StarRating
          value={0}
          onChange={(rating) => rateMutation.mutate({ gameId: current.gameId, rating })}
          size={22}
          ariaLabel={`Rate ${current.game.title}`}
        />
      </div>
      <div className="cg-rating-banner-actions">
        {unrated.length > 1 && (
          <button
            className="cg-btn ghost tiny"
            onClick={() => navigate('/played?filter=unrated-me')}
          >
            Rate the rest ({unrated.length})
          </button>
        )}
        <button
          className="cg-btn ghost tiny"
          onClick={() => dismiss(activeGroupId, newestMs)}
        >
          Maybe later
        </button>
        <button className="cg-rating-banner-never" onClick={hideForever}>
          Don&apos;t show again
        </button>
      </div>
    </div>
  )
}
